import React, { useState, useMemo } from 'react'
import { FormStepper } from 'components/form-stepper'
import { IncommOfferForm } from './incomm-offer-form/IncommOfferForm'
import { IncommOfferMultipleForm } from './incomm-offer-form/IncommOfferMultipleForm'
import { IncommOfferBarcodeResult } from './IncommOfferBarcodeResult'
import { IncommOfferLayout } from './IncommOfferLayout'
import {
  IncommOfferModalCheck,
  IncommOfferModalVariant,
} from './incomm-offer-modal-check/IncommOfferModalCheck'
import { GenerateBarcodeResponse } from '../../../services/incomm/barcode'
import {
  IncommStepperProps,
  normalizeOfferData,
  createBarcodeFormProps,
} from '../utils'
import analytics from 'global/AnalyticsData'

export const IncommOfferStep = ({
  step,
  nextStepButton,
  skipButton,
  onEngage,
  onAwardPoints,
}: IncommStepperProps) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [barcodeResponse, setBarcodeResponse] =
    useState<GenerateBarcodeResponse | null>(null)
  const [formError, setFormError] = useState<any>(null)
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalVariant, setStatusModalVariant] =
    useState<IncommOfferModalVariant>('barcode_limit')

  const normalizedData = useMemo(
    () => normalizeOfferData(step?.incomm),
    [step?.incomm],
  )

  const openStatusModal = (variant: IncommOfferModalVariant) => {
    setStatusModalVariant(variant)
    setStatusModalOpen(true)
  }

  // Add analytics tracking for step interaction
  const handleFormSubmit = async (data: any) => {
    try {
      setFormError(null)

      const detail = data?.detail as GenerateBarcodeResponse | undefined

      // Offer expired (backend may return 200 with status field, or include offerStatus/message)
      const offerStatus = (detail?.offerStatus || '').toString().toLowerCase()
      const message = (detail?.message || detail?.error || '').toString()
      const now = Date.now()
      const offerEndMs = detail?.offerEnd ? Date.parse(detail.offerEnd) : NaN
      const looksExpired =
        offerStatus.includes('expired') ||
        message.toLowerCase().includes('expired') ||
        (Number.isFinite(offerEndMs) &&
          offerEndMs < now &&
          !detail?.barcodeAccessToken)

      if (looksExpired) {
        // Prevent moving to results screen in expired case
        setBarcodeResponse(null)
        analytics.stepEvent('step_interaction_incomm_offer', step, {
          bannerStore: data.selectedStoreId || null,
          result: 'offer_expired',
          context: 'expired_offer',
        })
        openStatusModal('expired')
        return
      }

      const isSuccessfulEnoughToShowResult = Boolean(
        detail?.barcodeAccessToken ||
        detail?.barcodeContent ||
        detail?.displayText ||
        detail?.id,
      )

      if (isSuccessfulEnoughToShowResult && detail) {
        setBarcodeResponse(detail)
        setSelectedStoreId(data.selectedStoreId || null)
        setCurrentStep(1)
        onEngage()
        onAwardPoints()

        // Only show limit modal when limit > 1 (e.g. user tries to generate 3rd with limit=2)
        const limit = detail.quantityLimit
        const current = detail.currentQuantity
        const atOrOverLimit =
          detail.quantityLimitReached === true ||
          (typeof limit === 'number' &&
            typeof current === 'number' &&
            current >= limit)

        if (
          detail.isExistingBarcodeAtLimit === true &&
          typeof limit === 'number' &&
          limit > 1 &&
          atOrOverLimit
        ) {
          analytics.stepEvent('step_interaction_incomm_offer', step, {
            bannerStore: data.selectedStoreId || null,
            result: 'barcode_limit_reached',
            context: 'limit_exceeded',
            context_id: limit,
            context_position: current,
          })
          openStatusModal('barcode_limit')
        } else {
          // Successful barcode generation
          analytics.stepEvent('step_interaction_incomm_offer', step, {
            bannerStore: data.selectedStoreId || null,
            result: 'barcode_generated_success',
            context: 'barcode_success',
            result_id: detail.id || detail.barcodeAccessToken,
          })
        }
      } else {
        const errorMessage =
          (data?.detail as any)?.message ||
          (data?.detail as any)?.error ||
          'Failed to generate barcode'

        // Check if error message contains clipLimitExceeded
        if (errorMessage.toLowerCase().includes('cliplimitexceeded') ||
          errorMessage.toLowerCase().includes('clip limit exceeded')) {
          openStatusModal('clip_limit_exceeded')
          analytics.stepEvent('step_interaction_incomm_offer', step, {
            bannerStore: data.selectedStoreId || null,
            result: 'clip_limit_exceeded',
            context: 'error',
            context_id: 'clipLimitExceeded',
            result_id: errorMessage,
          })
        } else {
          setFormError(errorMessage)
          analytics.stepEvent('step_interaction_incomm_offer', step, {
            bannerStore: data.selectedStoreId || null,
            result: 'barcode_generation_failed',
            context: 'error',
            context_id: (data?.detail as any)?.error || 'unknown_error',
            result_id: errorMessage,
          })
        }
      }
    } catch (error) {
      console.error('Error processing barcode:', error)

      // Show generic error modal
      openStatusModal('service_unavailable')

      analytics.stepEvent('step_interaction_incomm_offer', step, {
        bannerStore: null,
        result: 'barcode_exception',
        context: 'exception',
        context_id: String(error),
        result_id: 'service_unavailable',
      })
    }
  }

  const handleCloseStatusModal = () => setStatusModalOpen(false)

  if (!normalizedData) {
    return null
  }

  const { config, isMultiple, singleOffer, offers, bannerImageUrl } =
    normalizedData

  const barcodeFormProps = createBarcodeFormProps(normalizedData, {
    selectedStoreId,
    formError,
    onSubmit: handleFormSubmit,
    onStatusModalOpen: openStatusModal,
  })

  const steps = [
    {
      id: 'form',
      name: 'Fill Form',
      component: isMultiple ? (
        <IncommOfferMultipleForm
          offers={offers}
          incommBarcodeForm={barcodeFormProps}
        />
      ) : (
        <IncommOfferForm
          offer={{
            ...singleOffer,
            id: singleOffer?.offerId || '',
            amount: 0,
            terms: config.legalDescription || '',
            expirationDate: config.expirationDate || '',
          }}
          incommBarcodeForm={barcodeFormProps}
        />
      ),
    },
    {
      id: 'results',
      name: 'View Results',
      component: (
        <IncommOfferBarcodeResult
          barcodeAccessToken={barcodeResponse?.barcodeAccessToken}
          barcodeText={barcodeResponse?.displayText}
          offerEnd={barcodeResponse?.offerEnd}
          selectedStoreId={selectedStoreId}
          email={barcodeResponse?.email}
          offerId={
            barcodeResponse?.offerId
              ? String(barcodeResponse.offerId)
              : undefined
          }
          stepId={step?.originalStepId}
          step={step}
          autoSendEmail={
            barcodeFormProps.autoSendEmail || barcodeResponse?.autoSendEmail
          }
          autoSendBarcodeEmail={barcodeResponse?.autoSendBarcodeEmail}
        />
      ),
    },
  ]

  return (
    <div>
      <IncommOfferLayout
        title={config.title || ''}
        description={config.description || ''}
        supportDescription={config.supportDescription || ''}
        terms={config.legalDescription || ''}
        backgroundImageUrl={bannerImageUrl}
        nextStepButton={nextStepButton}
        skipButton={skipButton}
      >
        <FormStepper
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          showProgress={false}
          hideBackButton
        />
      </IncommOfferLayout>

      <IncommOfferModalCheck
        variant={statusModalVariant}
        barcodeResponse={barcodeResponse}
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
      />
    </div>
  )
}

export default IncommOfferStep

export type {
  SingleOfferData,
  MultipleOffersData,
  IncommStepperProps,
} from '../utils'
